import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { LifeBuoy, Send, Mail, Clock } from "lucide-react";

// Kept in sync with CONTACT_CATEGORY_LABELS in backend/server.py and the category
// <select> on the public frontend/public/contact.html — same categories, same order.
const CATEGORIES = [
    { value: "general", label: "General Question" },
    { value: "technical", label: "Technical Issue" },
    { value: "billing", label: "Billing & Plans" },
    { value: "feature", label: "Feature Request" },
    { value: "partnership", label: "Partnership" },
    { value: "press", label: "Press / Media" },
    { value: "other", label: "Other" },
];

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">{label}</Label>
            {children}
        </div>
    );
}

export default function Contact() {
    const { user } = useAuth();
    const [form, setForm] = useState({
        name: user?.name || "",
        email: user?.email || "",
        category: "general",
        subject: "",
        message: "",
    });
    const [sending, setSending] = useState(false);

    const send = async () => {
        if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
            toast.error("Please fill in your name, email, subject, and message.");
            return;
        }
        setSending(true);
        try {
            // Public endpoint (no auth required) — see backend/server.py's POST /contact
            // and the standalone form at frontend/public/contact.html for the logged-out
            // equivalent of this same page.
            await api.post("/contact", {
                name: form.name.trim(), email: form.email.trim(),
                category: form.category, subject: form.subject.trim(), message: form.message.trim(),
            });
            toast.success("Message sent — we'll get back to you soon.");
            setForm(f => ({ ...f, subject: "", message: "" }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to send message. Please try again.");
        } finally { setSending(false); }
    };

    return (
        <div className="space-y-4 max-w-2xl" data-testid="contact-page">
            <div>
                <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
                    <LifeBuoy className="w-6 h-6 text-cyan-600" /> Contact Us
                </h1>
                <p className="text-sm text-slate-500 mt-1">Questions, feedback, or something not working right? Send us a message and we'll reply to your email.</p>
            </div>

            <div className="card-elev rounded-2xl p-6 space-y-5" data-testid="contact-form-card">
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Name">
                        <Input
                            data-testid="contact-name" value={form.name} maxLength={120}
                            onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your name"
                        />
                    </Field>
                    <Field label="Email">
                        <Input
                            data-testid="contact-email" type="email" value={form.email} maxLength={255}
                            onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@example.com"
                        />
                    </Field>
                </div>

                <Field label="What's this about?">
                    <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                        <SelectTrigger data-testid="contact-category" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </Field>

                <Field label="Subject">
                    <Input
                        data-testid="contact-subject" value={form.subject} maxLength={200}
                        onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="How can we help?"
                    />
                </Field>

                <Field label="Message">
                    <Textarea
                        data-testid="contact-message" value={form.message} maxLength={5000} rows={6}
                        onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Tell us a bit more…"
                        className="resize-none rounded-xl"
                    />
                </Field>

                <div className="flex flex-col-reverse sm:flex-row items-start sm:items-center justify-between gap-3 pt-1">
                    <div className="flex items-center gap-1.5 text-xs text-slate-400">
                        <Clock className="w-3.5 h-3.5" /> We usually reply within one business day.
                    </div>
                    <Button
                        onClick={send} disabled={sending} data-testid="contact-send"
                        className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-10 px-6 font-semibold gap-1.5"
                    >
                        {sending ? "Sending…" : <><Send className="w-4 h-4" /> Send message</>}
                    </Button>
                </div>
            </div>

            <p className="text-xs text-slate-400 flex items-center gap-1.5 px-1">
                <Mail className="w-3.5 h-3.5" /> You can also reach us directly at{" "}
                <a href="mailto:support@apptechcare.com" className="text-indigo-600 hover:text-indigo-700 font-semibold">support@apptechcare.com</a>.
            </p>
        </div>
    );
}
